/**
 * Learning Center API: the learner's catalog reads (plan docs/plans/lms-plan.md
 * Appendix A "Courses and catalog"; 7.2, 18.11, 18.17; WP-30's routes, WP-40's
 * client). Typed calls over services/api (the shared axios instance that adds
 * the JWT and X-Site-Id); nothing under src/lms imports axios (2.3a).
 *
 *   GET /courses?category=&limit=&offset=  -> { items, page }
 *   GET /courses/:id                       -> the course with its current version summary,
 *                                             objectives, sources and change summary (7.2 Overview)
 *   GET /courses/:id/content?version_id=   -> { version, content, quiz_settings }; the quiz
 *                                             pool never leaves the server, knowledge_check and
 *                                             scenario slides keep their correct and best flags (18.11)
 *
 * The player also reads the caller's open assignment for the course (GET
 * /me/assignments?status=open, WP-31) to learn the version it is pinned to and
 * whether it is a retrain or an acknowledge assignment (7.2: the change
 * summary comes first; 8.1: acknowledge mode), and an image slide's asset URL
 * (GET /assets/:id/url, named in 5.2).
 *
 * Query strings travel as axios `params`, so the URL itself stays the path
 * (the test harness routes GETs by exact URL).
 */
import api from '../../services/api';
import type { CourseCategory, CourseContent, CourseKind, CourseSource, VersionQuizSettings } from '../content/types';
import type { LmsList } from './client';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** GET /courses list item (Appendix A; WP-30 reads.js). */
export interface CourseListItem {
  id: string;
  slug: string;
  title: string;
  category: CourseCategory;
  kind: CourseKind;
  validity_months: number | null;
  estimated_minutes: number | null;
  current_version: { id: string; version_no: number; published_at: string | null } | null;
  is_active: boolean;
}

/** The current version summary on GET /courses/:id. */
export interface CourseVersionSummary {
  id: string;
  version_no: number;
  published_at: string | null;
  estimated_minutes?: number | null;
  retrain_policy?: string | null;
}

/** GET /courses/:id (Appendix A; 7.2 Overview). */
export interface CourseDetail {
  id: string;
  slug: string;
  title: string;
  category: CourseCategory;
  kind: CourseKind;
  description: string | null;
  validity_months: number | null;
  renewal_lead_days: number;
  is_active: boolean;
  current_version: CourseVersionSummary | null;
  objectives: string[];
  not_covered: string | null;
  disclaimer: string | null;
  sources: CourseSource[];
  change_summary: string | null;
}

/** GET /courses/:id/content (Appendix A, 18.11, 18.17). */
export interface CourseContentResponse {
  version: { id: string; version_no: number };
  content: CourseContent;
  /** A read_ack version's settings are {} (5.2). */
  quiz_settings: VersionQuizSettings;
}

/** 5.4: why an assignment exists. */
export type AssignmentSource = 'profile' | 'campaign' | 'renewal' | 'retrain' | 'acknowledge' | 'manual' | 'onboarding';

/** 5.4: full = take the course; acknowledge = read the change and attest (8.1). */
export type AssignmentMode = 'full' | 'acknowledge';

/** One GET /me/assignments item, the fields the player reads (WP-31 mine.js). */
export interface MyAssignment {
  id: string;
  course_id: string;
  source: AssignmentSource;
  /** Why it was assigned, in words (7.1). */
  source_label: string;
  required_version_id: string | null;
  mode: AssignmentMode;
  status: 'open' | 'completed' | 'waived' | 'cancelled';
  due_at: string;
  due_state: 'overdue' | 'due_soon' | 'upcoming' | null;
  /**
   * The version's change summary (7.2, shown first) for an assignment whose
   * source is retrain or acknowledge, or whose mode is acknowledge whatever
   * the source (a campaign sent under "acknowledge the change", plan 18.27;
   * mine.js showsChangeSummary, the player's changeSummaryFor), else null.
   */
  change_summary: string | null;
}

/** A presigned URL answer (2.3a "Downloads"). */
export interface PresignedUrl {
  url: string;
  filename?: string;
}

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** GET /api/lms/courses?category= (the network catalog; WP-42 lists it). */
export async function listCourses(
  options: { category?: CourseCategory | null; limit?: number; offset?: number } = {}
): Promise<LmsList<CourseListItem>> {
  const params: Record<string, string | number> = {};
  if (options.category) params.category = options.category;
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.offset !== undefined) params.offset = options.offset;
  const response = await api.get<LmsList<CourseListItem>>('/api/lms/courses', { params });
  return response.data;
}

/** GET /api/lms/courses/:id */
export async function getCourse(courseId: string): Promise<CourseDetail> {
  const response = await api.get<CourseDetail>(`/api/lms/courses/${enc(courseId)}`);
  return response.data;
}

/**
 * GET /api/lms/courses/:id/content, for the current published version, or
 * for an earlier published version named by versionId (an assignment pinned
 * to it, 8.1; 18.17).
 */
export async function getCourseContent(courseId: string, versionId?: string | null): Promise<CourseContentResponse> {
  const config = versionId ? { params: { version_id: versionId } } : undefined;
  const response = await api.get<CourseContentResponse>(`/api/lms/courses/${enc(courseId)}/content`, config);
  return response.data;
}

/**
 * The caller's open assignment for one course, or null (GET
 * /me/assignments?status=open; Appendix A caps a page at 200, and one person
 * holds at most one open assignment per course, 5.4).
 */
export async function getMyOpenAssignment(courseId: string): Promise<MyAssignment | null> {
  const response = await api.get<LmsList<MyAssignment>>('/api/lms/me/assignments', {
    params: { status: 'open', limit: 200 },
  });
  const items = Array.isArray(response.data?.items) ? response.data.items : [];
  return items.find((a) => a.course_id === courseId && a.status === 'open') ?? null;
}

/** GET /api/lms/assets/:id/url: the presigned URL of an image slide's asset (5.2). */
export async function getAssetUrl(assetId: string): Promise<PresignedUrl> {
  const response = await api.get<PresignedUrl>(`/api/lms/assets/${enc(assetId)}/url`);
  return response.data;
}
